import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { InspectionPlanTaskStatus } from 'app/viewmodels/analysis/inspection-plan-status.enum';
import { InspectionPlanTask } from 'app/viewmodels/analysis/inspection-plan-task';
import { InspectionPlanTaskMitigationColumn } from 'app/viewmodels/analysis/inspection-plan-task-mitigation-column';
import * as moment from 'moment';
import { LazyLoadEvent } from 'primeng/api';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AuditEntityService } from '../../core/audit-entity.service';
import { BackendService } from '../../core/backend.service';
import { CONFIG } from '../../core/config';
import { StaticLookupService } from '../../core/static-lookups/static-lookup.service';
import { HbNotification } from '../../notifications/notification';
import { NotificationState } from '../../notifications/notification-state';
import { NotificationService } from '../../notifications/notification.service';
import { MitigationsProviderService } from '../../runnable-models/recommendations/mitigations-provider.service';
import { ErpMitigationJob } from '../../viewmodels/admin/integration/erp-mitigation-job';
import { ErpMitigationJobs } from '../../viewmodels/admin/integration/erp-mitigation-jobs';
import { DeferralTask } from '../../viewmodels/analysis/deferral-task';
import { ErpDropdownOption } from '../../viewmodels/analysis/erp-dropdown-options';
import { InspectionPlanTaskErpFieldType } from '../../viewmodels/analysis/inspection-plan-task-erp-field-type.enum';
import { MitigationPlanTaskSource } from '../../viewmodels/analysis/inspection-plan-task-source';
import { LastInspectionDateSource } from '../../viewmodels/analysis/last-inspection-date-source';
import { MitigationPlanFrequencySource } from '../../viewmodels/analysis/mitigation-plan-frequency-source';
import { Recommendation } from '../../viewmodels/analysis/recommendation';
import { Recommendations } from '../../viewmodels/analysis/recommendations';
import { DropdownItem } from '../../viewmodels/dropdown-item';
import { MitigationPlanTaskFrequencyPipe } from '../pipes/mitigation-plan-task-frequency.pipe';
import { DeferralStatusType } from './deferral-status-type';

@Injectable({
  providedIn: 'root',
})
export class MitigationPlanService {
  private unsubscribe: Subject<boolean> = new Subject<boolean>();

  public currentMitigationPlan: Recommendations;

  public erpDropdownsOptionsData: ErpDropdownOption[] = [];
  public mappedErpDropdownsOptionsData: { [key: string]: DropdownItem[] } = {};
  private isErpDropdownsPopulated: boolean = false;
  private readonly erpDropdowns = [
    { id: 'erpAvailability', sourceLookupId: CONFIG.constants.erpDropdownColumnsLookups.erpAvailability },
    { id: 'erpMaintenanceActivityType', sourceLookupId: CONFIG.constants.erpDropdownColumnsLookups.erpMaintenanceActivityType },
    { id: 'erpPlannerGroup', sourceLookupId: CONFIG.constants.erpDropdownColumnsLookups.erpPlannerGroup },
    { id: 'erpWorkCenter', sourceLookupId: CONFIG.constants.erpDropdownColumnsLookups.erpWorkCenter },
  ];

  /**
   * The orgTreeNodeId for the Asset that the current Mitigation Plan is for.
   * Used to get and reload the Mitigation Plan.
   */
  private _orgTreeNodeId: string;

  get orgTreeNodeId(): string {
    return this._orgTreeNodeId;
  }

  get selectedItemName(): string {
    return this.mitigationProviderService.selectedItemName;
  }

  private _staticColumns: any[] = [];

  get staticColumns(): any[] {
    return this._staticColumns;
  }

  set staticColumns(value: any[]) {
    this._staticColumns = value;
  }

  /**
   * List of Mitigation Jobs retreived from the API. Used to populate the dialog shown
   * in the component.
   */
  public erpMaintenanceJobs: ErpMitigationJobs;

  /**
   * The currently selected job. Highlighted in the list in the dialog.
   */
  public selectedErpJob: ErpMitigationJob;

  /**
   * Used to propagate the dropdown of Inspection Priorities when creating a new Ad-Hoc Task.
   */
  public inspectionPriorities: string[];

  public erpIdentifier: string;
  public erpEnabledTaskCount: number;

  inspectionPlanTaskStatusEnum = InspectionPlanTaskStatus;

  public mitigationPlanIsLoaded = new Subject();

  constructor(
    private backendService: BackendService,
    private auditEntityService: AuditEntityService,
    private i18NextPipe: I18NextPipe,
    private staticLookupService: StaticLookupService,
    private mitigationProviderService: MitigationsProviderService,
    private notificationService: NotificationService
  ) { }


  /**
   * Sets the value of currentMitigationPlan based on an API call using orgTreeNodeId.
   * @param {string} orgTreeNodeId Identifies the associated Asset.
   */
  public loadMitigationPlan(orgTreeNodeId: string, Event?: LazyLoadEvent): void {
    this._orgTreeNodeId = orgTreeNodeId;
    const requestPayload = {
      first: 1,
      rows: 10,
      selectedItemId: orgTreeNodeId,
      filters: [],
      sortField: null,
      sortOrder: Event?.sortOrder ?? 1,
      skip: Event?.first ?? 0,
      take: Event?.rows ?? 10,

      searchtext: Array.isArray(Event?.globalFilter?.searchText) ? (Event.globalFilter.searchText as string[]).join(',') : '1,2'
    };

    this.loadMitigationData(orgTreeNodeId, requestPayload)
      .subscribe(
        res => {
          this.setMitigationPlanWhenLoaded(res);
          this.mitigationPlanIsLoaded.next();
        },
        error => {
          this.currentMitigationPlan.items = [];
          console.error('Error loading data:', error);
        }
      );
  }

  public loadMitigationData(orgTreeNodeId: string, requestPayload: any) {
    this._orgTreeNodeId = orgTreeNodeId;

    return this.backendService
      .postObjectRoute<never>(
        CONFIG.apiRoutes.currentRbiInspectionPlanByOrgTreeNode + '/' + orgTreeNodeId,
        requestPayload,
        true
      ).pipe(
        map(res => {
          if (res) {
            const item = new Recommendations();

            item.loadFromJson(res);

            return item;
          }
        }),
      );
  }

  /**
   * Adds a new Mitigation Plan Task to the currently-active Mitigation Plan.
   * @param {Recommendation} The task to add to the plan.
   */
  public createMitigationPlanTask(newTask: Recommendation, pageEvent: LazyLoadEvent): void {
    this.auditEntityService.auditNew(newTask);

    if (newTask.lastInspectionDate) {
      newTask.lastInspectionDateSource = LastInspectionDateSource.Manual;
    }
    else {
      newTask.lastInspectionDateSource = LastInspectionDateSource.InspectionReport;
    }

    newTask.frequencySource = MitigationPlanFrequencySource.RecommendationFrequency;
    newTask.orgTreeNodeId = this._orgTreeNodeId;
    this.createAdHocMitigationColumns(newTask);
    this.backendService.create<InspectionPlanTask>(InspectionPlanTask, newTask).subscribe((data: Recommendation) => {
      newTask.inspectionPlanTaskId = data.inspectionPlanTaskId;
      newTask.setPermissions();

      if (newTask.sourceType === MitigationPlanTaskSource.AdHoc) {
        this.currentMitigationPlan.items.push(newTask);
      }

      if (this.currentMitigationPlan) {
        this.loadMitigationPlan(this._orgTreeNodeId, pageEvent);
      }
    });
  }

  public createDeferralTask(newTask: Recommendation, revisedDueDate: string, loggedInUser:string, eventId: string): Observable<DeferralTask> {
    const deferralTask = this.createDeferralTaskFromRecommendation(newTask, revisedDueDate, loggedInUser, eventId);
    return this.backendService.create<DeferralTask>(DeferralTask, deferralTask);
  }

  private createDeferralTaskFromRecommendation(recommendation: Recommendation, revisedDueDate: string, loggedInUser:string, eventId: string): DeferralTask {
    const deferralTask = new DeferralTask();
    deferralTask.inspectionPlanTaskId = recommendation.inspectionPlanTaskId;
    deferralTask.taskReference = recommendation.taskReference;
    deferralTask.inspectionPriority = recommendation.mitigationType;
    deferralTask.taskExtent = recommendation.taskExtent;
    deferralTask.taskFrequency = recommendation.taskFrequency;
    deferralTask.taskInterval = recommendation.taskInterval;
    deferralTask.overrideFrequency = recommendation.overrideFrequency;
    deferralTask.lastInspectionDate = recommendation.lastInspectionDate;
    deferralTask.lastInspectionDateOverriden = recommendation.lastInspectionDateOverriden;
    deferralTask.dueDate = recommendation.dueDate ? new Date(recommendation.dueDate) : null;
    deferralTask.revisedDueDate = revisedDueDate ? new Date(revisedDueDate) : null;
    deferralTask.dueDateOverridden = recommendation.isStaticDueDate;
    deferralTask.deferralStatus = DeferralStatusType.PendingApproval;
    deferralTask.createdBy = loggedInUser;
    deferralTask.creationDate = new Date().toISOString();
    deferralTask.lastModifiedBy = loggedInUser;
    deferralTask.lastModifiedDate = new Date().toISOString();
    deferralTask.masterInspectionMethodId = recommendation.masterInspectionMethodId;
    deferralTask.eventId = eventId;

    return deferralTask;
  }



  private createAdHocMitigationColumns(newTask: InspectionPlanTask): void {
    const mitigationColumns: InspectionPlanTaskMitigationColumn[] = [];

    if (this.currentMitigationPlan && this.currentMitigationPlan.mitigationColumns) {
      this.currentMitigationPlan.mitigationColumns.forEach(e => {
        mitigationColumns.push(this.createInspectionPlanTaskMitigationColumn('', e.mitigationColumnId, newTask.inspectionPlanTaskId));
      });
    }

    newTask.mitigationColumns = mitigationColumns;
  }

  private createInspectionPlanTaskMitigationColumn(
    cellText: string,
    mitigationColumnId: string,
    inspectionPlanTaskId: string,
  ): InspectionPlanTaskMitigationColumn {
    const mitigationColumn = new InspectionPlanTaskMitigationColumn();

    mitigationColumn.cellText = cellText;
    mitigationColumn.mitigationColumnId = mitigationColumnId;
    mitigationColumn.inspectionPlanTaskId = inspectionPlanTaskId;

    return mitigationColumn;
  }

  /**
   * Used to save task details when editing an existing task.
   * @param {Recommendation} item The task whose details have been edited.
   * @param {boolean} loadMitigationPlan Whether or not to reload the current Mitigation Plan after saving.
   */
  public saveTask(task: Recommendation, loadMitigationPlan: boolean, pageEvent: LazyLoadEvent): void {
    this.auditEntityService.auditExisting(task);
    this.disableSendErpForAllApartCurrent(task);
    this.backendService.update<InspectionPlanTask>(InspectionPlanTask, task.inspectionPlanTaskId, task).subscribe({
      next: () => {
        if (loadMitigationPlan) {
          this.loadMitigationPlan(this._orgTreeNodeId, pageEvent);
        }
        this.showSaveNotification(NotificationState.Complete);
      },
      error: (err) => {
        const errorMessage = Array.isArray(err.messages)
          ? err.messages.map((msg: any) => msg.text).join(', ')
          : err.messages;
        this.showNotification(errorMessage, NotificationState.Failed);
        console.error('Error saving task:', err);
      }}
    );
  }

  public updateRecommendationDeferralStatus(taskId: string, deferralStatus: DeferralStatusType, eventId: string, pageEvent: LazyLoadEvent): void {
    this.backendService
      .postObjectRoute(
        CONFIG.apiRoutes.inspectionPlanTaskDeferralStatusUpdate + '/' + taskId,
        deferralStatus
      )
      .subscribe({
        next: (next) => {
          // Reload the mitigation plan to reflect the updated deferral status
          this.loadMitigationPlan(this._orgTreeNodeId, pageEvent);
        },
        error: (err) => console.error('Error updating deferral status:', err)
      });
  }

  /**
   * Makes an API call to retreive the ERP Job Plan for the current Mitigation Plan.
   * @param {Recommendation} task The task that we're using for the query. Required
   * to set one of the ERP fields after the API call completes.
   */
  public getMitigationJobs(task: Recommendation): Observable<void> {
    return this.backendService.getById<ErpMitigationJobs>(ErpMitigationJobs, this._orgTreeNodeId).pipe(
      map(erpMaintenanceJobs => {
        if (erpMaintenanceJobs) {
          this.erpMaintenanceJobs = erpMaintenanceJobs;
          this.selectedErpJob = null;
        }
      }),
    );
  }

  /**
   * Makes a call to the lookup service to populate the dropdown list options.
   */
  public getInspectionPriorities(): void {
    this.inspectionPriorities = [];
    // Note: The type is 'MitigationType' for historical reasons, InspectionPriority is the correct current parlance
    this.staticLookupService.getByIdAsync('MitigationType').subscribe(data => {
      data.forEach(element => {
        this.inspectionPriorities.push(element.value);
      });
    });
  }

  private setMitigationPlanWhenLoaded(res: Recommendations): void {
    if (res) {
      this.currentMitigationPlan = res;
      this.erpEnabledTaskCount = this.currentMitigationPlan.items.filter(x => x.sendToErp === true).length;
    }
  }

  private disableSendErpForAllApartCurrent(task: Recommendation): void {
    if (task.sendToErp) {
      const { items } = this.currentMitigationPlan;

      items.forEach(e => {
        if (e.inspectionPlanTaskId !== task.inspectionPlanTaskId) {
          e.sendToErp = false;
        }
      });
    }
  }

  public savePlanTaskMitigationColumn(planTaskMitigationColumn: InspectionPlanTaskMitigationColumn): void {
    this.backendService
      .update<InspectionPlanTaskMitigationColumn>(
        InspectionPlanTaskMitigationColumn,
        planTaskMitigationColumn.inspectionPlanTaskMitigationColumnId,
        planTaskMitigationColumn as InspectionPlanTaskMitigationColumn,
        false,
      )
      .subscribe(() => { });
  }

  public UpdateTaskStatus(tasks: Recommendation[], taskStatus: InspectionPlanTaskStatus, copyMeasurementData: boolean, pageEvent: LazyLoadEvent): void {
    const payload = {
      TaskIds: tasks.map(task => task.inspectionPlanTaskId),
      InspectionPlanTaskStatusType: taskStatus,
      CopyMeasurementData: copyMeasurementData
    };

    this.backendService
      .postObjectRoute(CONFIG.apiRoutes.inspectionPlanTaskStatusUpdate, payload, true)
      .subscribe({
        next: () => {
          this.loadMitigationPlan(this._orgTreeNodeId, pageEvent);
        },
        error: (err) => {
          this.showNotification(err.reasonPhrase, NotificationState.Failed);
          console.error('Error updating task status:', err);
        }
      }
      );
  }
  public GetSimilarTaskWithErpDetails(rowData: Recommendation): Promise<Recommendation> {
    return new Promise(resolve => {
      this.backendService.getAnyById(CONFIG.apiRoutes.getSimilarInspectionPlanTaskWithErp, rowData.inspectionPlanTaskId)
        .subscribe((data: Recommendation) => {
          resolve(data || null);
        }, error => {
          console.error('Error fetching similar task with ERP details:', error);
          resolve(null);
        });
    });
  }

  public updateTaskErpColumn(taskId: string, erpColumnType: InspectionPlanTaskErpFieldType, erpColumnValue: string): void {
    const parameters = {
      erpColumnType,
      erpColumnValue
    };
    this.backendService
      .postObjectRoute(
        CONFIG.apiRoutes.inspectionPlanTaskErpUpdate + '/' + taskId,
        parameters
      )
      .subscribe(result => {
        return result;
      }
      );
  }

  public populateErpDropdowns(): void {
    // Check if the dropdowns are already populated or if the length is this.erpDropdowns.length
    if (this.isErpDropdownsPopulated || this.erpDropdownsOptionsData.length >= this.erpDropdowns.length) {
      return;
    }

    const dropdownRequests = this.erpDropdowns.map(dropdown =>
      this.loadLookupDisplayValues(dropdown.sourceLookupId, dropdown.id)
    );

    forkJoin(dropdownRequests)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dropdownResults => {
        this.erpDropdownsOptionsData = dropdownResults.filter(Boolean); // Remove nulls if any
        this.initializeOptionsData();
        this.isErpDropdownsPopulated = true;
      });
  }

  private loadLookupDisplayValues(
    sourceLookupId: string,
    dropdownTypeName: string
  ): Observable<ErpDropdownOption | null> {

    return this.staticLookupService
      .getAllLookupDisplayValuesById(sourceLookupId)
      .pipe(
        map(result => {
          if (!result?.length) return null;

          const dropdownData = new ErpDropdownOption();
          dropdownData.options = result.map(lookupDisplayValue => ({
            value: lookupDisplayValue.displayValue,
            key: lookupDisplayValue.lookupRowId,
            title: '',
          }));
          dropdownData.dropdownTypeName = dropdownTypeName;
          return dropdownData;
        }),
        catchError(err => {
          console.error(`Failed to load ${dropdownTypeName}:`, err);
          return of(null); // Allow other dropdowns to load
        })
      );
  }

  private initializeOptionsData(): void {
    this.erpDropdownsOptionsData.forEach(dropdown => {
      this.mappedErpDropdownsOptionsData[dropdown.dropdownTypeName] = [
        { label: 'None', value: null }, // Add 'None' as the first option
        ...dropdown.options.map(opt => ({
          label: opt.value,
          value: opt.value,
        })),
      ];
    });
  }

  //Below is the code for exporting the Mitigation Plan to Excel
  public async exportMitigationTableData(Event: LazyLoadEvent): Promise<void> {
    const exportableData = await this.prepareExportableData(Event);
    const headers = this.prepareHeaders();
    const data = this.prepareExcelData(exportableData, headers);

    this.exportToExcel(data);
  }


  private async prepareExportableData(event: LazyLoadEvent): Promise<any[]> {
    let totalRecords = this.currentMitigationPlan?.totalRecords;
    const requestPayload = {
      first: 1,
      rows: 10,
      selectedItemId: this._orgTreeNodeId,
      filters: [],
      sortField: null,
      sortOrder: event?.sortOrder ?? 1,
      skip: 0,
      take: totalRecords ?? event?.rows ?? 10,

      searchtext: Array.isArray(event?.globalFilter?.searchText) ? (event.globalFilter.searchText as string[]).join(',') : '1,2'
    };

    const recommendations = await this.loadMitigationData(this._orgTreeNodeId, requestPayload).toPromise();

    if (!recommendations) {
      return [];
    }

    return recommendations.items.map(row => {
      const formattedRow = { ...row } as any;
      if (formattedRow.inspectionPlanTaskStatus?.name) {
        formattedRow.inspectionPlanTaskStatus = formattedRow.inspectionPlanTaskStatus.name;
      }
      if (formattedRow.frequencySource !== undefined && formattedRow.frequencySource !== null) {
        const frequencyPipe = new MitigationPlanTaskFrequencyPipe();
        formattedRow.frequencySource = frequencyPipe.transform(formattedRow.frequencySource);
      }
      if (recommendations.mitigationColumns) {
        recommendations.mitigationColumns.forEach(col => {
          formattedRow[col.headerText] = row.mitigationColumns?.find(
            (item: InspectionPlanTaskMitigationColumn) => item.mitigationColumnId === col.mitigationColumnId
          )?.cellText ?? '';
        });
      }
      return formattedRow;
    });
  }

  private prepareHeaders(): { staticHeaders: string[]; staticFields: string[]; dynamicHeaders: string[] } {
    const staticHeaders = this.staticColumns.filter(col => col.display).map(col => col.header);
    const staticFields = this.staticColumns.filter(col => col.display).map(col => col.field);
    const dynamicHeaders = this.currentMitigationPlan.mitigationColumns?.map(col => col.headerText) || [];
    return { staticHeaders, staticFields, dynamicHeaders };
  }

  // Here we are preparing the data for the Excel file. Mapping the row data to the headers.
  private prepareExcelData(
    exportableData: any[],
    headers: { staticHeaders: string[]; staticFields: string[]; dynamicHeaders: string[] }
  ): any[] {
    return exportableData.map(row => {
      const formattedRow: any = {};
      headers.staticFields.forEach((field, index) => {
        let value = row[field];
        // Format if value is a Date object
        value = this.formatDate(value);
        formattedRow[headers.staticHeaders[index]] = value ?? '';


      });
      headers.dynamicHeaders.forEach(header => {
        let value = row[header];
        value = this.formatDate(value);
        formattedRow[header] = value ?? '';
      });
      return formattedRow;
    });
  }
  private formatDate(value: any): any {
    if (value instanceof Date) {
      value = moment(value).format('YYYY-MM-DD');
    }

    // Or format if value is an ISO date string
    else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      value = moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

  private exportToExcel(data: any[]): void {
    import('xlsx').then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(data);
      const workbook = { Sheets: { MitigationPlan: worksheet }, SheetNames: ['MitigationPlan'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });

      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const fileName = `${this.selectedItemName}_Mitigation_Plan_${moment().format('YYYY-MM-DD')}.xlsx`;

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    });
  }

  public showSaveNotification(state: NotificationState) {
    this.showNotification(this.i18NextPipe.transform('MitigationPlan.TaskSaved'), state);
  }

  public showNotification(description: string, state: NotificationState) {
    const notification = new HbNotification();
    notification.description = description;
    notification.state = state;
    this.notificationService.displayToast(notification);
  }
}
